// No zod here: this module is imported by AppProviders, so a zod import drags
// ~64 KB gz of schema machinery into every page's client bundle (app router
// fails to tree-shake it) just to length-check a string.
export const VANITY_URL_TREATMENTS = ['1m_dupecoin_giveaway'] as const

// One roster for every podcast/creator vanity slug. Two behaviors hang off it:
//   • `code` (+ altCodes) → the `?promo=CODE` dupecoin-giveaway modal (AppProviders).
//   • `vanitySlug` (+ vanityAliases) → a clean top-level `/slug` that 302s to
//     the homepage with a DUCI `utm_campaign` for marketing attribution (see
//     creatorVanityRedirects.ts). Aliases (misspellings, long forms) redirect
//     with the MAIN slug's DUCI, so one creator's traffic stays on one DUCI.
// An entry can have either or both. `code`/`treatment` are optional so a creator
// we only want a DUCI vanity link for (no giveaway) can omit them.
export const VANITY_URL_PROMOS: {
  name: string
  network?: string
  type?: string
  code?: string
  altCodes?: string[]
  vanitySlug?: string
  vanityAliases?: string[]
  treatment?: (typeof VANITY_URL_TREATMENTS)[number]
}[] = [
  {
    name: 'Bad Friends',
    network: 'YMH Studios',
    type: 'Podcast',
    code: 'BADFRIENDS',
    altCodes: ['BAD-FRIENDS', 'BAD_FRIENDS', 'BAD FRIENDS'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Dear Shandy',
    network: 'Podcast Nation',
    type: 'Podcast',
    code: 'SHANDY',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Have A Seat with Chris Hansen',
    network: 'Red Seat Ventures',
    type: 'Podcast',
    code: 'HANSEN',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: "I've Had It",
    network: 'PodcastOne',
    type: 'Podcast',
    code: 'HADIT',
    altCodes: ['HAD-IT', 'HAD_IT', 'HAD IT'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'IHIP News',
    network: 'PodcastOne',
    type: 'Podcast',
    code: 'IHIP',
    altCodes: ['IHIP-NEWS', 'IHIP_NEWS', 'IHIP NEWS'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Internet Today Daily',
    network: 'The Roost',
    type: 'Podcast',
    code: 'ITDAILY',
    altCodes: ['IT-DAILY', 'IT_DAILY', 'IT DAILY'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Majority 54',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'MAJORITY',
    altCodes: ['MAJORITY-54', 'MAJORITY_54', 'MAJORITY 54'],
    vanitySlug: 'majority',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Megyn Kelly Show',
    network: 'Red Seat Ventures',
    type: 'Podcast',
    code: 'MK',
    altCodes: ['MEGAN', 'MEGYN'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'MeidasTouch',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'MEIDAS',
    altCodes: ['MEIDAS-TOUCH', 'MEIDAS_TOUCH', 'MEIDAS TOUCH'],
    vanitySlug: 'meidas',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Neebscast',
    network: 'The Roost',
    type: 'Podcast',
    code: 'NEEBS',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'On Democracy',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'FRED',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Piers Morgan Uncensored',
    network: 'Red Seat Ventures',
    type: 'Podcast',
    code: 'PIERS',
    altCodes: ['PIERS-MORGAN', 'PIERS_MORGAN', 'PIERS MORGAN'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Political Beatdown with Michael Cohen and Ben Meiselas',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'BEAT',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: "Sorry We're Cyrus",
    network: 'Podcast Nation',
    type: 'Podcast',
    code: 'CYRUS',
    vanitySlug: 'cyrus',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Tech Newsday',
    network: 'The Roost',
    type: 'Podcast',
    code: 'NEWSDAY',
    altCodes: ['NEWS-DAY', 'NEWS_DAY', 'NEWS DAY'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Tennessee Brando',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'BRANDO',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'The Bravo Docket',
    network: 'Podcast Nation',
    type: 'Podcast',
    code: 'DOCKET',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'The Ken Harbaugh Show',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'KEN',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'The PoliticsGirl Podcast',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'POLITICSGIRL',
    altCodes: ['POLITICS-GIRL', 'POLITICS_GIRL', 'POLITICS GIRL'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'The Tim Dillon Show',
    network: 'Audioboom',
    type: 'Podcast',
    code: 'TIM',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'The Weekend Show',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'WEEKEND',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'Uncovered',
    network: 'Meidas Touch',
    type: 'Podcast',
    code: 'UNCOVERED',
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'You Made it Weird with Pete Holmes',
    network: 'You Made it Weird',
    type: 'Podcast',
    code: 'WEIRD',
    altCodes: ['WIERD', 'WIRED'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'The Brett Cooper Show',
    network: 'Red Seat Ventures',
    type: 'Podcast',
    code: 'BRETT',
    altCodes: ['BRET'],
    treatment: '1m_dupecoin_giveaway',
  },
  {
    name: 'H3 Podcast',
    network: 'H3 Productions',
    type: 'Podcast',
    code: 'H3',
    treatment: '1m_dupecoin_giveaway',
  },
  // Vanity-redirect-only creators (DUP-1749) — a clean `/slug` DUCI link, no
  // dupecoin-giveaway promo modal. No `code`, so they never surface in the
  // `?promo=` modal flow; `name`/`network` are informational only.
  {
    name: 'East Fam',
    type: 'Podcast',
    vanitySlug: 'eastfam',
  },
  {
    name: 'The Bulwark',
    network: 'The Bulwark',
    type: 'Podcast',
    vanitySlug: 'bulwark',
  },
  {
    name: 'The Danny Jones Podcast',
    network: 'Danny Jones',
    type: 'Podcast',
    vanitySlug: 'dannyjones',
  },
  // DUP-3487. YouTube @poppiesstudios. The aliases catch likely misspellings
  // and the channel handle; all of them track as `poppies`.
  {
    name: 'Poppies',
    network: 'Poppies Studios',
    type: 'YouTube',
    vanitySlug: 'poppies',
    vanityAliases: [
      'poppy',
      'poppie',
      'poppys',
      'popies',
      'poppiesstudios',
      'poppies-studios',
      'poppiesstudio',
      'poppystudios',
    ],
  },
  // Not a creator: a paid listing on outbid.lol, which rewrites the query string
  // on every outbound link (all 62 listings resolve to exactly `?utm_source=outbid`),
  // so a UTM-tagged URL cannot carry our DUCI. The path survives, so the slug is
  // the only attribution channel that works there.
  {
    name: 'outbid.lol',
    network: 'outbid.lol',
    type: 'Paid placement',
    vanitySlug: 'outbid',
  },
] as const

const VANITY_CODES = new Set(
  VANITY_URL_PROMOS.flatMap((promo) => [
    ...(promo.code ? [promo.code.toLowerCase()] : []),
    ...(promo.altCodes || []).map((code) => code.toLowerCase()),
  ])
)

// Entries that get a top-level `/slug` DUCI redirect, each with its aliases.
// Exported for creatorVanityRedirects.ts (the DUCI + redirect logic) and its
// route-collision test — this list is the single source of truth for creator
// vanity slugs and aliases.
export const CREATOR_VANITY_LINKS = VANITY_URL_PROMOS.flatMap((promo) =>
  promo.vanitySlug
    ? [{ slug: promo.vanitySlug, aliases: promo.vanityAliases ?? [] }]
    : []
)

export const CREATOR_VANITY_SLUGS = CREATOR_VANITY_LINKS.map(({ slug }) => slug)

const minVanityCodeLength = Math.min(
  ...Array.from(VANITY_CODES).map((code) => code.length)
)
const maxVanityCodeLength = Math.max(
  ...Array.from(VANITY_CODES).map((code) => code.length)
)

export const isVanityUrlCode = (code: string) => {
  const trimmed = code.trim()
  if (
    trimmed.length < minVanityCodeLength ||
    trimmed.length > maxVanityCodeLength
  ) {
    return false
  }

  return VANITY_CODES.has(trimmed.toLowerCase())
}

export const getVanityUrlPromo = (code: string) => {
  const primary = VANITY_URL_PROMOS.find(
    (promo) => promo.code?.toLowerCase() === code.toLowerCase()
  )
  if (primary) {
    return primary
  }

  // Fallback for mispelled or alt codes etc
  const alt = VANITY_URL_PROMOS.find((promo) =>
    (promo.altCodes || [])
      .map((c) => c.toLowerCase())
      .includes(code.toLowerCase())
  )
  return alt
}
